import { useMemo } from 'react';
import { Axis } from '@visx/axis';
import { utcDay, utcWeek, utcMonth, utcYear } from 'd3-time';

import { VisxText } from '@/components/VisxText';

import { useTheme } from '@simplywallst/ui-core';

import type { scaleLinear, scaleUtc } from '@visx/scale';
import type { Period } from '../../types';

const getTimelineAxisTicks = (period: Period) => {
  if (period === '7D') return utcDay.every(1);
  if (period === '1M') return utcWeek.every(1);
  if (period === '3M') return utcMonth.every(1);
  if (period === '1Y') return utcMonth.every(3);
  if (period === 'YTD') return utcMonth.every(2);
  return utcYear.every(1);
};

interface Props {
  period: Period;
  xScale: ReturnType<typeof scaleUtc<number>>;
  yScale: ReturnType<typeof scaleLinear<number>>;
}

export const XAxis = ({ xScale, yScale, period }: Props) => {
  const theme = useTheme();

  const tickArray = useMemo(() => {
    const interval = getTimelineAxisTicks(period);
    return interval !== null ? xScale.ticks(interval) : xScale.ticks(1);
  }, [period, xScale]);

  return (
    <Axis scale={xScale} tickValues={tickArray}>
      {(axis) => {
        const [chartHeight, chartPaddingTop] = yScale.range();
        return (
          <g transform={`translate(0, ${chartPaddingTop + chartHeight})`}>
            <rect
              y="0"
              height="1"
              width="100%"
              fill={theme.color.white}
              fillOpacity={0.3}
            />
            {axis.ticks.map((tick) => (
              <g
                key={`x-axis-${tick.to.x}-${tick.value}`}
                transform={`translate(${tick.to.x}, 1)`}
              >
                <rect
                  width="1"
                  height="4"
                  fill={theme.color.white}
                  x="0"
                  y="0"
                  fillOpacity={0.3}
                />
                <VisxText
                  x="0"
                  y="8"
                  textAnchor="middle"
                  verticalAnchor="start"
                  opacity={0.3}
                  typography="tiny"
                  fill={theme.color.white}
                >
                  {tick.formattedValue}
                </VisxText>
              </g>
            ))}
          </g>
        );
      }}
    </Axis>
  );
};
